import classNames from 'classnames'
import { IGraphData } from '../../../../../modules/apis/student/lesson'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { n_ai_analysis } from '../../../../../modules/i18n/strings/auto/wording'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'

interface CafJobAnalysisBarType {
  graphData: IGraphData
  percentile: number
}

export const CafJobAnalysisBar = ({ graphData, percentile }: CafJobAnalysisBarType) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const dividerArray = [25, 50, 75]
  const axisText = [`${t(n_ai_analysis.job_seg_level.top)} 75%`, '50%', '25%']
  const analysisData = [graphData.top, graphData.average, graphData.bottom]

  // percentile 툴팁이 그래프 양쪽 끝에서 일정 이상 넘어가지 않게 제한
  const boundedPercentileText = Math.min(94.5, Math.max(6.5, 100 - percentile))

  return (
    <div className={barStyles.container1}>
      <div className={barStyles.gray_bar}>
        <div className={barStyles.graph}>
          <div className={barStyles.active} style={{ width: 100 - percentile + '%' }}></div>
        </div>
        <div className={barStyles.percentile} style={{ left: 100 - percentile + '%' }}></div>
        {/* DIVIDER */}
        {dividerArray?.map((dividerValue, idx) => (
          <div key={idx + 'dividerValue'} className={barStyles.divider} style={{ left: dividerValue + '%' }}></div>
        ))}
        <div className={barStyles.percentile_arrow} style={{ left: 100 - percentile + '%' }}></div>
        <div className={barStyles.percentile_text} style={{ left: boundedPercentileText + '%' }}>
          {t(n_ai_analysis.job_seg_level.top)} {percentile}%
        </div>
      </div>
      <div className={barStyles.axis}>
        {axisText.map((text, i) => (
          <span className={barStyles.axisContainer} key={i + text}>
            <span className={barStyles.axisText1}>{text}</span>
            <span className={barStyles.axisText2}>Lv {analysisData[i]}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export const CafJobAnalysisBarSimple = ({ graphData, percentile }: CafJobAnalysisBarType) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const dividerArray = [25, 50, 75]
  const axisText = [`${t(n_ai_analysis.job_seg_level.top)} 75%`, '50%', '25%']
  const analysisData = [graphData.top, graphData.average, graphData.bottom]

  // percentile 툴팁이 그래프 양쪽 끝에서 일정 이상 넘어가지 않게 제한
  const boundedPercentileText = Math.min(94.5, Math.max(6.5, 100 - percentile))

  return (
    <div className={barStyles.container1}>
      <div className={barStyles.gray_bar}>
        <div className={barStyles.graph}>
          <div className={barStyles.active} style={{ width: 100 - percentile + '%' }}></div>
        </div>
        <div className={barStyles.percentile} style={{ left: 100 - percentile + '%' }}></div>
        {/* DIVIDER */}
        {dividerArray?.map((dividerValue, idx) => (
          <div key={idx + 'dividerValue'} className={barStyles.divider} style={{ left: dividerValue + '%' }}></div>
        ))}
      </div>
    </div>
  )
}

interface CafAnalysisBoxType {
  title: string
  levelExact: number
  graphData: IGraphData
  percentile: number
}

export const CafJobAnalysisBox = ({ title, levelExact, graphData, percentile }: CafAnalysisBoxType) => {
  const { t, locale } = useTranslation()

  return (
    <div className={classNames(boxStyles.container1, `border-1 box-border border-gray-200`)}>
      {/* HEADER */}
      <div className={boxStyles.container2}>
        <div className="flex items-center gap-[4px]">
          <div className={boxStyles.title}>{title}</div>
          {locale === 'ko' && <div className={boxStyles.subtitle}>{t(n_caf.highlight_list[title + '2'])}</div>}
        </div>
        <div className="flex flex-row items-center justify-end gap-[8px]">
          <span className={boxStyles.spanBody}>{t(n_ai_analysis.job_seg_level.my_average)}</span>
          <span className={boxStyles.spanLevel}>{levelExact}</span>
        </div>
      </div>
      {/* PROGRESS BAR */}
      <CafJobAnalysisBar graphData={graphData} percentile={percentile} />
    </div>
  )
}

export const CafJobAnalysisBoxSimple = ({ title, levelExact, graphData, percentile }: CafAnalysisBoxType) => {
  const { t, locale } = useTranslation()

  return (
    <div className="w-full flex flex-col gap-[8px]">
      {/* HEADER */}
      <div className={boxStyles.container2}>
        <div className="flex items-center gap-[4px]">
          <div className="Body2-14Bd text-gray-900">{title}</div>
          {locale === 'ko' && <div className="Caption-12Md text-gray-400">{t(n_caf.highlight_list[title + '2'])}</div>}
        </div>
        <div className="flex flex-row items-center justify-end gap-[8px]">
          <span className="Caption-12Md text-gray-400">{t(n_ai_analysis.job_seg_level.my_average)}</span>
          <span className="Body2-14Bd text-gray-800">{levelExact}</span>
          <span className="Caption-12Md text-gray-200">|</span>
          <span className="Body2-14Bd text-gray-800">
            {t(n_ai_analysis.job_seg_level.top) + ' '}
            {percentile}%
          </span>
        </div>
      </div>
      {/* PROGRESS BAR */}
      <CafJobAnalysisBarSimple graphData={graphData} percentile={percentile} />
    </div>
  )
}

const barStyles = {
  container1: `flex flex-col gap-[4px]`,
  gray_bar: `relative z-[1] flex h-[24px] w-full overflow-visible rounded-full bg-gray-200`,
  graph: `absolute top-2 left-2 z-[1] flex h-[20px] w-full overflow-hidden rounded-full transition-colors`,
  active: `absolute z-[2] h-[20px] bg-gradient-to-r from-purple-400 to-blue-400 transition-colors`,
  divider: `absolute z-[3] h-[24px] w-[4px] bg-gray-50`,
  percentile: `absolute z-[4] my-[2px] h-[20px] w-[20px] -translate-x-1/2 transform overflow-visible rounded-full border-4 border-gray-800 bg-white transition-colors`,
  percentile_arrow: `absolute top-[-8.5px] z-[1] h-[10px] w-[10px] -translate-x-1/2 -translate-y-1/2 rotate-45 transform rounded-sm bg-gray-800 transition-colors`,
  percentile_text: `absolute top-[-6px] z-[2] h-[23px] w-max -translate-x-1/2 -translate-y-full transform rounded bg-gray-800 py-[2px] px-[6px] text-[12px] font-bold text-white transition-colors`,
  axis: `flex w-[100%] flex-row items-center justify-between`,
  axisContainer: `flex flex-row gap-[4px]`,
  axisText1: `text-[12px] font-bold text-gray-500`,
  axisText2: `text-[12px] font-bold text-gray-400`,
}

const boxStyles = {
  container1: `flex w-full flex-col gap-[24px] rounded-[8px] bg-gray-50 py-[12px] px-[16px] shadow-sm sm:w-[408px]`,
  container2: `flex w-full flex-row items-start justify-between`,
  title: `Title2-18Bd capitalize text-gray-700`,
  subtitle: `Body2-14Md ml-[1px] text-gray-500`,
  spanBody: `Caption-12Md text-gray-400`,
  spanLevel: `Title1-20Bd text-gray-800`,
  img: `ml-[8px] mb-[6px] inline`,
}
